import { clsx } from 'clsx';
import type { LeadStatus, LeadSource } from '@/types';

type BadgeVariant = 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'orange' | 'gray' | 'cyan' | 'indigo' | 'teal';

const variantClasses: Record<BadgeVariant, string> = {
  blue: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  green: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  yellow: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  purple: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
  orange: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  gray: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
  cyan: 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200',
  indigo: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
  teal: 'bg-teal-50 text-teal-700 ring-1 ring-teal-200',
};

export const STATUS_COLORS: Record<LeadStatus, BadgeVariant> = {
  new: 'blue',
  followup: 'yellow',
  opd_scheduled: 'cyan',
  opd_done: 'green',
  ipd_scheduled: 'purple',
  ipd_done: 'indigo',
  insurance: 'orange',
  preauth: 'teal',
  emi: 'blue',
  closed_won: 'green',
  closed_lost: 'red',
  not_interested: 'gray',
};

export const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New Lead',
  followup: 'Follow Up',
  opd_scheduled: 'OPD Scheduled',
  opd_done: 'OPD Done',
  ipd_scheduled: 'IPD Scheduled',
  ipd_done: 'IPD Done',
  insurance: 'Insurance',
  preauth: 'Pre-Auth',
  emi: 'EMI',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  not_interested: 'Not Interested',
};

export const SOURCE_COLORS: Record<LeadSource, BadgeVariant> = {
  meta: 'blue',
  google: 'red',
  seo: 'green',
  gmb: 'orange',
  referral: 'purple',
  walk_in: 'teal',
  call: 'yellow',
  website: 'cyan',
  other: 'gray',
};

interface BadgeProps {
  variant?: BadgeVariant;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md';
}

export function Badge({ variant = 'gray', children, className, size = 'sm' }: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center font-medium rounded-full',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        variantClasses[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StatusBadge({ status }: { status: LeadStatus }) {
  return (
    <Badge variant={STATUS_COLORS[status]}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

export function SourceBadge({ source }: { source: LeadSource | null | undefined }) {
  if (!source) return null;
  return (
    <Badge variant={SOURCE_COLORS[source]}>
      {source.toUpperCase()}
    </Badge>
  );
}
